/* eslint-disable @typescript-eslint/indent */

/* Note: please be careful with indentation, we have the rule disabled because of
*  eslint not being able to properly indent js inside styled components
*/

// Core
import styled from 'styled-components';

// Libraries
import { Upload as UploadFileAntD } from 'antd';

// Component
import { FileUploadProps } from './FileUpload.types';

// Plugins
import { hexToRgba } from '../../../plugins/general';

// Types
import { Theme } from '../../../types/theme';

const getBorderColor = (
  error: FileUploadProps['error'],
  source: FileUploadProps['file']['source'],
  dragging: boolean,
  theme: Theme,
) => {
  let { borderColor } = theme.color.fileUpload.default;
  if (error && source) {
    borderColor = theme.color.fileUpload.error.borderColor;
  } else if (dragging) {
    borderColor = theme.color.fileUpload.dragging.borderColor;
  }

  return `1px solid ${borderColor}`;
};

const getBackgroundColor = (
  error: FileUploadProps['error'],
  source: FileUploadProps['file']['source'],
  dragging: boolean,
  disabled: FileUploadProps['disabled'],
  theme: Theme,
): string => {
  let { backgroundColor } = theme.color.fileUpload;
  if (error && source) {
    backgroundColor = theme.color.fileUpload.error.backgroundColor;
  } else if (dragging) {
    backgroundColor = theme.color.fileUpload.dragging.backgroundColor;
  } else if (disabled) {
    backgroundColor = theme.color.fileUpload.disabledBackgroundColor;
  }

  return backgroundColor;
};

const getClickToUploadTextColor = (
  dragging: boolean,
  disabled: FileUploadProps['disabled'],
  theme: Theme,
): string => {
  let textColor = theme.color.fileUpload.default.clickToUploadTextColor;
  if (dragging) {
    textColor = theme.color.fileUpload.draggingTextColor;
  } else if (disabled) {
    textColor = theme.color.fileUpload.disabledTextColor;
  }

  return textColor;
};

const getIconColor = (
  dragging: boolean,
  disabled: FileUploadProps['disabled'],
  theme: Theme,
): string => {
  let { iconColor } = theme.color.fileUpload;
  if (dragging) {
    iconColor = theme.color.fileUpload.draggingIconColor;
  } else if (disabled) {
    iconColor = theme.color.fileUpload.disabledIconColor;
  }
  return iconColor;
};

export const TryAgainText = styled.div`
  color: ${({ theme }) => theme.color.fileUpload.errorTextColor};
  font-family: ${({ theme }) => theme.font.bold};
  height: 22px;
  width: max-content;
  margin: 0;
  padding: 0;
  transition: color 250ms ease-in-out;
  
  :hover {
    cursor: pointer;
    color: ${({ theme }) => theme.color.fileUpload.errorHoverTextColor};
  };
`;

export const UploadedContainer = styled.div<Omit<FileUploadProps, 'id' | 'onUpload' | 'onRemove'>>`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  user-select: none;
  
  background-color: ${({ theme, error, file }) => (
    file.source && error
      ? theme.color.fileUpload.error.backgroundColor
      : theme.color.fileUpload.uploaded.backgroundColor
  )};
  border: 1px solid ${({ theme, error, file }) => (
    file.source && error
      ? theme.color.fileUpload.error.borderColor
      : theme.color.fileUpload.uploaded.borderColor
  )};
`;

export const FilePreview = styled.div`
  position: relative;
  height: max-content;
  width: max-content;

  > svg {
    width: 16px;
    height: 16px;
    position: absolute;
    top: -8px;
    left: -8px;
    > path {
      fill: ${({ theme }) => theme.color.fileUpload.selectedFileIconColor};
    };
  };
`;

export const ImagePreview = styled.img`
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 6px;
`;

export const FileUploaded = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.color.fileUpload.iconBackgroundColor};
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  > svg {
    width: 18px;
    height: 18px;
    > path {
      fill: ${({ theme }) => theme.color.fileUpload.iconColor};
    };
  };
`;

export const Message = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  height: 100%;
`;

export const RemoveFile = styled.div<Pick<FileUploadProps, 'error'>>`
  align-self: ${({ error }) => (error ? 'flex-start' : 'center')};
  color: ${({ error, theme }) => (
    error
      ? theme.color.fileUpload.errorRemoveIconColor
      : theme.color.fileUpload.removeIconColor
  )};
  width: 16px;
  height: 16px;
  
  > svg {
    width: 16px;
    height: 16px;
    transition: color 250ms ease-in-out;
    
    :hover {
      color: ${({ theme, error }) => (
        error
          ? theme.color.fileUpload.errorHoverRemoveIconColor
          : theme.color.fileUpload.removeHoverIconColor
      )};
      cursor: pointer;
    };
  }
`;

export const UploadContainer = styled.div<
  Omit<FileUploadProps, 'id' | 'onUpload' | 'onRemove'> & { dragging: boolean }
>`
  display: flex;
  z-index: 1;
  height: 100%;
  position: relative;
  padding: 16px;
  border-radius: 8px;
  box-sizing: border-box;
  gap: 8px;

  border: ${({
    error,
    file,
    dragging,
    theme,
  }) => getBorderColor(error, file.source, dragging, theme)};
  
  background-color: ${({
    error,
    file,
    dragging,
    disabled,
    theme,
  }) => getBackgroundColor(error, file.source, dragging, disabled, theme)};
`;

export const UploadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
`;

export const IconContainer = styled.div<{ dragging: boolean } & Pick<FileUploadProps, 'disabled'>>`
  width: 32px;
  height: 32px;
  border-radius: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 4px solid ${({ theme, dragging }) => (
    dragging
     ? hexToRgba(theme.color.fileUpload.draggingBackgroundColor, 0.4)
     : theme.color.fileUpload.iconBackgroundColor
  )};
  background-color: ${({ theme, dragging }) => (
    dragging
      ? hexToRgba(theme.color.fileUpload.draggingBorderColor, 0.2)
      : theme.color.fileUpload.iconBackgroundColor
  )};
  
  > svg {
    width: 20px;
    height: 20px;
    > path {
      fill: ${({ theme, dragging, disabled }) => getIconColor(dragging, disabled, theme)};
    };
  };
`;

export const DragArea = styled.div`
  box-sizing: border-box;
  height: 100%;
  z-index: 2;
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
`;

export const Element = styled(UploadFileAntD)<Omit<FileUploadProps, 'id' | 'onUpload' | 'onRemove'> & {
  dragging: boolean,
}>`
  /* *********************** CLICK TO UPLOAD *********************** */
  strong {
    color: ${({
      theme,
      dragging,
      disabled,
    }) => (getClickToUploadTextColor(dragging, disabled, theme))};
    
    font-family: ${({ theme }) => theme.font.bold};
  };
  
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  flex-flow: row wrap;
  cursor: pointer;
  position: relative;
  border-radius: 8px;
  
  .ant-upload {
    width: 100%;
    position: absolute;
    height: 100%;
    right: 0;
    top: 0;
    box-sizing: border-box;
  };
  
  span.ant-upload {
    box-sizing: border-box;
  };
  
  height: ${({ error, file }) => (error && file.source ? '115px' : '84px')};
  
  .ant-upload-disabled {
    cursor: ${({ file }) => (file.source ? 'auto' : 'not-allowed')};
  };

  .ant-upload-list-item-container {
    display: none;
  };
  
  ${FileUploaded} {
    width: ${({ error }) => error && '32px'};
    height: ${({ error }) => error && '32px'};
    border-radius: ${({ error }) => error && '28px'};
    background-color: ${({
      error,
      theme,
    }) => error && theme.color.fileUpload.errorIconBackgroundColor};
    
    > svg > path {
      fill: ${({ theme, error }) => error && theme.color.fileUpload.errorIconColor};
    };
  };
`;
